/**
 * For the accounting reports (used with inventoryReportMixin): the chart-of-accounts picker, Dr / Cr balances
 * (the server sends balances as Dr − Cr), voucher-type badges and a print table that keeps statement headings bold.
 * The component's `filters` hold `search` besides its own filters.
 */
import { escapeHtml } from '../inventory/inventoryReportMixin';

export const TYPE_TONES = {
  sale: 'success', sales_return: 'danger', receipt: 'success', purchase: 'info', purchase_return: 'warning',
  payment: 'warning', opening: 'muted', damage: 'danger', transfer: 'muted', debit: 'warning', credit: 'success',
  journal: 'info', contra: 'muted', other: 'muted',
};

/** Levels of a statement: main groups, sub-groups or every account. */
export const DEPTH_CHOICES = [
  { id: 1, text: 'Main groups' },
  { id: 2, text: 'Sub-groups' },
  { id: null, text: 'All accounts' },
];

export default {
  computed: {
    accountOptions() {
      return (this.options.accounts || []).map((a) => ({
        id: a.id,
        text: `${'\u00a0\u00a0'.repeat(a.level)}${a.name} (${a.code})${a.group ? ' · group' : ''}`,
      }));
    },
  },

  methods: {
    drcr(value) {
      const n = Number(value) || 0;
      return n > 0.005 ? 'Dr' : n < -0.005 ? 'Cr' : '';
    },

    balanceText(value) {
      const n = Number(value) || 0;
      return Math.abs(n) < 0.005 ? '0.00' : `${this.formatMoney(Math.abs(n))} ${this.drcr(n)}`;
    },

    amountText(value) {
      const n = Number(value) || 0;
      return Math.abs(n) < 0.005 ? '—' : this.formatMoney(n);
    },

    typeTone(key) {
      return TYPE_TONES[key] || 'muted';
    },

    accountName(id) {
      return this.optionName(this.options.accounts || [], id, '—');
    },

    storeLabel() {
      return `Store: ${this.options.store || '—'}`;
    },

    /** Opens the General Ledger tab of the report hub on an account. */
    openLedger(accountId) {
      if (!accountId) return;
      this.$router.replace({ query: { section: 'finance', report: 'general_ledger', account: accountId } });
    },

    /**
     * Prints `rows` as a table. columns: [{ label, value(row) -> text, num? }]; rows with `_bold` print bold;
     * `totals` maps column labels to the totals row's text (the first column says "Total" otherwise).
     */
    printStatement(title, metaParts, cards, columns, rows, totals = null) {
      const cell = (column, text) => `<td${column.num ? ' class="num"' : ''}>${escapeHtml(text ?? '')}</td>`;
      const head = columns.map((c) => `<th${c.num ? ' class="num"' : ''}>${escapeHtml(c.label)}</th>`).join('');
      const body = rows.map((row) => `<tr${row._bold ? ' style="font-weight:bold;background:#F2F4F7"' : ''}>${
        columns.map((c) => cell(c, c.value(row))).join('')}</tr>`).join('');
      const foot = totals
        ? `<tfoot><tr>${columns.map((c, i) => cell(c, totals[c.label] ?? (i === 0 ? 'Total' : ''))).join('')}</tr></tfoot>`
        : '';
      this.openPrint(
        title,
        [metaParts.filter(Boolean).join(' · '), `Printed ${new Date().toLocaleString()}`],
        cards.map(([label, value]) => [label, escapeHtml(value)]),
        `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody>${foot}</table>`,
      );
    },
  },
};
